import { DEFAULT_CLIENT_RETRY, DEFAULT_CLIENT_TIMEOUT } from '../constants'

import { InterceptorPipeline } from './interceptor-pipeline'
import { KyTransportAdapter } from './transport'

import type {
  CreateClientOptions,
  HttpClientConfig,
  HttpResponse,
  HttpRequest,
  RequestOptions,
  HttpMethod,
  RetryConfig,
  RetryOption,
} from '../types'

const ACCEPT_CONTENT_TYPE = 'application/json, text/plain, */*'

type NormalizedHttpClientConfig = Omit<HttpClientConfig, 'retry'> & { retry: RetryConfig | false }

function normalizeRetry(value: RetryOption | undefined): RetryConfig | false {
  if (value === undefined || value === false || value === 0) return false
  if (typeof value === 'number') return { ...DEFAULT_CLIENT_RETRY, maxRetries: value }
  return { ...DEFAULT_CLIENT_RETRY, ...value }
}

export class HttpClient {
  private readonly config: NormalizedHttpClientConfig
  private readonly transport: KyTransportAdapter
  private readonly pipeline: InterceptorPipeline | null

  constructor(config: HttpClientConfig) {
    const retry = config.retry === undefined ? false : normalizeRetry(config.retry)

    this.config = {
      ...config,
      timeout: config.timeout ?? DEFAULT_CLIENT_TIMEOUT,
      retry,
    }

    this.transport = new KyTransportAdapter({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: this.config.headers,
      retry: this.config.retry,
      hooks: this.config.hooks,
    })

    this.pipeline =
      this.config.interceptors != null && this.config.interceptors.length > 0
        ? this.config.interceptors.reduce((p, i) => p.use(i), new InterceptorPipeline())
        : null
  }

  get<T>(path: string, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('GET', path, undefined, opts)
  }

  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('POST', path, body, opts)
  }

  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('PUT', path, body, opts)
  }

  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('PATCH', path, body, opts)
  }

  delete<T>(path: string, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('DELETE', path, undefined, opts)
  }

  private async request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<HttpResponse<T>> {
    const mergedRetry = opts?.retry !== undefined ? normalizeRetry(opts.retry) : this.config.retry

    const defaultHeaders = this.config.getDefaultHeaders?.(this.config.baseURL) ?? {}
    const acceptHeader = { Accept: ACCEPT_CONTENT_TYPE }
    const httpRequest: HttpRequest = {
      url: path,
      method,
      headers: { ...acceptHeader, ...defaultHeaders, ...this.config.headers, ...opts?.headers },
      body,
      signal: opts?.signal,
      timeout: opts?.timeout ?? this.config.timeout,
      searchParams: opts?.searchParams,
      retry: mergedRetry,
    }

    if (this.pipeline != null) {
      return this.pipeline.execute<T>(httpRequest, this.transport)
    }
    return this.transport.execute<T>(httpRequest)
  }
}

export function createClient(baseURL: string, options?: CreateClientOptions): HttpClient {
  const config: HttpClientConfig = { baseURL, ...options }
  return new HttpClient(config)
}
